import React, { useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';

import {
  DndContext,
  useDraggable,
  useDroppable,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  getDraggableItemsById,
  HIGHEST_PRIORITY,
  LOWEST_PRIORITY,
} from './utils';
import { DownArrowSvg } from './DownArrowSvg';
import {
  ArrowAndSlotContainer,
  DownArrow,
  DragAndDropContainer,
  DraggableContent,
  DraggableContentAndTitle,
  DraggableItem,
  DraggableTitle,
  DropColumn,
  DropRightColumnWrapper,
  PriorityTitle,
  Slot,
} from './InnovationTool.css';

interface Props {
  fieldId: string;
  values: Record<string, any>;
  handleChange: {
    (e: React.ChangeEvent<any>): void;
    <T = string | React.ChangeEvent<any>>(
      field: T
    ): T extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void;
  };
}

function Draggable({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
      }
    : { opacity: isDragging ? 0.5 : 1 };
  return (
    <DraggableItem
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
    >
      {children}
    </DraggableItem>
  );
}

function SortableItem({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
      }
    : { opacity: isDragging ? 0.5 : 1 };
  return (
    <DraggableItem
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
    >
      {children}
    </DraggableItem>
  );
}

function Droppable({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <DropColumn
      ref={setNodeRef}
      style={{
        backgroundColor: id === 'left-column' ? 'unset' : '#fff',
        border: isOver && id === 'left-column' ? '2px dashed #757575' : 'none',
        paddingRight: id === 'right-column' ? '25px' : '0',
      }}
    >
      {children}
    </DropColumn>
  );
}

export const DragAndDrop: React.FC<Props> = ({ fieldId, handleChange }) => {
  const draggableItems = getDraggableItemsById(fieldId);
  const SLOT_COUNT = draggableItems.length;

  const [itemLocations, setItemLocations] = useState<
    Record<string, 'left' | 'right'>
  >(draggableItems.reduce((acc, item) => ({ ...acc, [item.id]: 'left' }), {}));
  const [rightOrder, setRightOrder] = useState<string[]>([]);

  const isMobile = useMediaQuery('(max-width:600px)');

  // On mobile, initialize rightOrder with all items and set their location to 'right'
  useEffect(() => {
    if (isMobile) {
      setRightOrder(draggableItems.map((i) => i.id));
      setItemLocations((prev) => {
        const updated = { ...prev };
        draggableItems.forEach((item) => {
          updated[item.id] = 'right';
        });
        return updated;
      });
    }
  }, [isMobile, draggableItems]);

  function updateValues(newOrder: string[]) {
    handleChange({
      target: {
        name: fieldId,
        value: newOrder.join(','),
      },
    } as React.ChangeEvent<any>);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Sorting logic
    if (
      itemLocations[activeId] === 'right' &&
      rightOrder.includes(activeId) &&
      rightOrder.includes(overId) &&
      activeId !== overId
    ) {
      setRightOrder((prev) => {
        const oldIndex = prev.indexOf(activeId);
        const newIndex = prev.indexOf(overId);
        const newOrder = arrayMove(prev, oldIndex, newIndex);
        updateValues(newOrder);
        return newOrder;
      });
      return;
    }

    // Desktop logic for moving between columns
    if (!isMobile) {
      if (
        (overId === 'right-column' || rightOrder.includes(overId)) &&
        itemLocations[activeId] !== 'right'
      ) {
        setItemLocations((prev) => ({ ...prev, [activeId]: 'right' }));
        setRightOrder((prev) => {
          let newOrder;
          if (rightOrder.includes(overId)) {
            const newIndex = rightOrder.indexOf(overId);
            const filtered = prev.filter((id) => id !== activeId);
            filtered.splice(newIndex, 0, activeId);
            newOrder = filtered;
          } else {
            newOrder = [...prev, activeId];
          }
          updateValues(newOrder);
          return newOrder;
        });
        return;
      }

      if (overId === 'left-column' && itemLocations[activeId] !== 'left') {
        setItemLocations((prev) => ({ ...prev, [activeId]: 'left' }));
        setRightOrder((prev) => {
          const newOrder = prev.filter((id) => id !== activeId);
          updateValues(newOrder);
          return newOrder;
        });
        return;
      }
    }
  }

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <DragAndDropContainer>
        {!isMobile && (
          <Droppable id='left-column'>
            {draggableItems
              .filter((item) => itemLocations[item.id] === 'left')
              .map((item) => (
                <Draggable key={item.id} id={item.id}>
                  <DraggableContentAndTitle>
                    <DraggableTitle>{item.title}</DraggableTitle>
                    <DraggableContent>{item.content}</DraggableContent>
                  </DraggableContentAndTitle>
                </Draggable>
              ))}
          </Droppable>
        )}
        <DropRightColumnWrapper>
          <PriorityTitle style={{ marginTop: '10px' }}>
            {HIGHEST_PRIORITY}
          </PriorityTitle>
          <ArrowAndSlotContainer>
            {!isMobile && (
              <DownArrow>
                <DownArrowSvg />
              </DownArrow>
            )}
            <Droppable id='right-column'>
              <SortableContext
                items={rightOrder}
                strategy={verticalListSortingStrategy}
              >
                {[...Array(SLOT_COUNT)].map((_, idx) => {
                  const id = rightOrder[idx];
                  const item = draggableItems.find((i) => i.id === id);
                  return (
                    <Slot key={idx}>
                      {item && (
                        <SortableItem id={item.id}>
                          <DraggableContentAndTitle>
                            <DraggableTitle>{item.title}</DraggableTitle>
                            <DraggableContent>{item.content}</DraggableContent>
                          </DraggableContentAndTitle>
                        </SortableItem>
                      )}
                    </Slot>
                  );
                })}
              </SortableContext>
            </Droppable>
          </ArrowAndSlotContainer>
          <PriorityTitle style={{ marginBottom: '10px' }}>
            {LOWEST_PRIORITY}
          </PriorityTitle>
        </DropRightColumnWrapper>
      </DragAndDropContainer>
    </DndContext>
  );
};
